import { c as _c } from "react/compiler-runtime";
/**
 * Task-related data fetchers
 */
import { useQueryClient, QueryClient, useQuery, useMutation, useMutationState, queryOptions } from "@tanstack/react-query";
import { resetSplitDocumentConfidence } from "@/src/actions/file";
import { updateStatus, updateTask, updateAssignedUser, unassignAllMyTasks, updateTaskNote, updateTaskTags, updatePriority, deleteTask } from "@/src/actions/task";
import { ToastAction } from "@/src/components/shadcn/ui/toast";
import { useTaskFilters, type TaskFilters } from "@/src/components/tasks/use-task-filters";
import { toast } from "@/src/hooks/use-toast";
import { isTaskAgentRunActive, TASK_AGENT_RUN_POLL_INTERVAL_MS } from "@/src/lib/data-fetchers/agent-fetchers";
import { PAGINATED_ORDERS_CACHE_KEY, SINGLE_ORDER_CACHE_KEY } from "@/src/lib/data-fetchers/order-query-keys";
import { logger } from "@/src/lib/logging/logger";
import { type ClassificationSection } from "@/src/models/constants/classifications";
import { FileType } from "@/src/models/file";
import { BlockKey } from "@/src/models/intake/blocks/block-types";
import { Patient } from "@/src/models/patient";
import type { ConfirmInsuranceNetworkRequest } from "@/src/models/provider-insurance-requirements";
import { SchedulingRecommendation } from "@/src/models/scheduling-recommendation";
import type { VerifySchedulingRecommendationInput } from "@/src/models/scheduling-recommendation";
import { Section } from "@/src/models/section";
import { Task, TaskStatus, TaskPriority, TaskType, pastTenseStatus, completedStatuses } from "@/src/models/task";
import { type TaskFacetCountsKey, type TaskFiltersParams } from "@/src/models/task-filters";
import { TaskMetadata, taskToTaskMetadata } from "@/src/models/task-metadata";
import { type UpsertTaskStepVerification } from "@/src/models/task-step-verification";
import { type VerificationResult } from "@/src/models/task-verification";
import { confirmInsuranceNetwork } from "@/src/requests/task";
import { getAllFileTasks, getCanResumeAwaitingInformationTask, getWorkflowStepResumeCheck, getChildTasks, getTaskById, getTasksMetadata, getPaginatedTasksMetadata, getDocumentClassificationFilterSlugs, getTaskFacetCounts, getTaskHistory, getTaskSchedulingRecommendation, getTaskApproval, approveTask, continueAfterManualTprStep, getTaskStepVerifications, overrideTprReceivingAssignment, upsertTaskStepVerification, regenerateTaskSchedulingRecommendation, resumeAwaitingInformationTask, resumeWorkflowStep, verifyAndComplete, verifySchedulingEligibilityTask, sendReferringProviderEmail, type PaginatedTasksMetadataResponse } from "@/src/requests/task";
import { fetchJSON, isNotFound } from "@/src/requests/utils/fetch-json";

// Covers every task a patient realistically accumulates, in one page.
const PATIENT_TASKS_PAGE_SIZE = 500;
export const TASK_CACHE_KEY = "task";
export const TASK_BY_ID_CACHE_KEY = "task-by-id";
export const TASKS_CACHE_KEY = "tasks";
export const PAGINATED_TASKS_METADATA_CACHE_KEY = "paginated-tasks-metadata";
export const TASK_CLASSIFICATION_FILTER_SLUGS_CACHE_KEY = "task-classification-filter-slugs";
export const TASK_FACET_COUNTS_CACHE_KEY = "task-facet-counts";
export const CONTINUE_TASK_MUTATION_KEY = ["continue-task"] as const;
export const TASK_COMPLETION_MUTATION_KEY = ["task-completion"] as const;
export const TASK_APPROVAL_CACHE_KEY = "task-approval";
export const TASK_STEP_VERIFICATION_CACHE_KEY = "task-step-verification";
export const CLASSIFICATIONS_QUERY_KEY = ["classifications"] as const;
export const getTaskSchedulingRecommendationQueryKey = (taskId: string | null | undefined) => ["taskSchedulingRecommendation", taskId] as const;
export const TASK_CHILDREN_CACHE_KEY = "task-children";
export function useTaskRelativesLookup() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      fetchTaskById: taskId => queryClient.fetchQuery({
        queryKey: [TASK_BY_ID_CACHE_KEY, taskId],
        queryFn: () => getTaskById(taskId)
      }),
      fetchChildTasks: taskId_0 => queryClient.fetchQuery({
        queryKey: [TASK_CHILDREN_CACHE_KEY, taskId_0],
        queryFn: () => getChildTasks(taskId_0)
      })
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useTaskApproval(taskId, taskVersion) {
  const $ = _c(9);
  let t0;
  if ($[0] !== taskId || $[1] !== taskVersion) {
    t0 = [TASK_APPROVAL_CACHE_KEY, taskId, taskVersion];
    $[0] = taskId;
    $[1] = taskVersion;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== taskId) {
    t1 = () => getTaskApproval(taskId);
    $[3] = taskId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = Boolean(taskId);
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}
export function useApproveTaskMutation() {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = async (_result, t1) => {
      const {
        taskId: taskId_0
      } = t1;
      await Promise.all([queryClient.invalidateQueries({
        queryKey: [TASK_APPROVAL_CACHE_KEY, taskId_0]
      }), queryClient.invalidateQueries({
        queryKey: [TASK_CACHE_KEY]
      }), queryClient.invalidateQueries({
        queryKey: [TASKS_CACHE_KEY]
      })]);
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      invalidates: [[TASK_APPROVAL_CACHE_KEY], [TASK_CACHE_KEY], [TASKS_CACHE_KEY]]
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = {
      mutationFn: _temp,
      onSuccess: t0,
      meta: t1
    };
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}
function _temp(t0) {
  const {
    taskId,
    approved
  } = t0;
  return approveTask(taskId, {
    approved
  });
}
export function useContinueAfterManualTprStepMutation() {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = async (_result, t1) => {
      const {
        taskId: taskId_0
      } = t1;
      await Promise.all([queryClient.invalidateQueries({
        queryKey: [TASK_CACHE_KEY, taskId_0]
      }), queryClient.invalidateQueries({
        queryKey: [TASK_BY_ID_CACHE_KEY, taskId_0]
      }), queryClient.invalidateQueries({
        queryKey: [TASKS_CACHE_KEY]
      })]);
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      invalidates: [[TASK_CACHE_KEY], [TASK_BY_ID_CACHE_KEY], [TASKS_CACHE_KEY]]
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = {
      mutationFn: _temp2,
      onSuccess: t0,
      meta: t1
    };
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}

/**
 * Recorded Correct / Not correct verdicts for this task's step cards, keyed by stable `stepKey`.
 */
function _temp2(t0) {
  const {
    taskId,
    stepKey,
    bypass
  } = t0;
  return continueAfterManualTprStep(taskId, stepKey, bypass);
}
export function useTaskStepVerifications(taskId) {
  const $ = _c(12);
  let t0;
  let t1;
  if ($[0] !== taskId) {
    t0 = [TASK_STEP_VERIFICATION_CACHE_KEY, taskId];
    t1 = () => getTaskStepVerifications(taskId as string);
    $[0] = taskId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(taskId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data,
    isPending
  } = useQuery(t3);
  let t4;
  if ($[7] !== data) {
    t4 = new Map((data ?? []).map(_temp3));
    $[7] = data;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isPending || $[10] !== t4) {
    t5 = {
      verdictByStepKey: t4,
      isPending
    };
    $[9] = isPending;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * Applies ops' provider/location override and replays the provider-scoped decisions downstream.
 * Invalidates the task caches because the workflow goes back to PROCESSING and the step rows the
 * instructions render from are rewritten server-side.
 */
function _temp3(row) {
  return [row.stepKey, row];
}
export function useOverrideTprReceivingAssignmentMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: async (_result, t1) => {
        const {
          taskId: taskId_0
        } = t1;
        await Promise.all([queryClient.invalidateQueries({
          queryKey: [TASK_CACHE_KEY, taskId_0]
        }), queryClient.invalidateQueries({
          queryKey: [TASK_BY_ID_CACHE_KEY, taskId_0]
        }), queryClient.invalidateQueries({
          queryKey: [TASKS_CACHE_KEY]
        }), queryClient.invalidateQueries({
          queryKey: [TASK_STEP_VERIFICATION_CACHE_KEY, taskId_0]
        })]);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp4(t0) {
  const {
    taskId,
    providerNpi,
    targetDepartmentId
  } = t0;
  return overrideTprReceivingAssignment(taskId, {
    providerNpi,
    targetDepartmentId
  });
}
export function useUpsertTaskStepVerificationMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp5,
      onSuccess: async (_result, t1) => {
        const {
          taskId: taskId_0
        } = t1;
        await queryClient.invalidateQueries({
          queryKey: [TASK_STEP_VERIFICATION_CACHE_KEY, taskId_0]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp5(t0) {
  const {
    taskId,
    payload
  } = t0;
  return upsertTaskStepVerification(taskId, payload);
}
async function getClassifications(): Promise<ClassificationSection[]> {
  return fetchJSON<ClassificationSection[]>("/api/classifications");
}
export function getClassificationsQueryOptions() {
  return queryOptions({
    queryKey: CLASSIFICATIONS_QUERY_KEY,
    queryFn: getClassifications,
    staleTime: Infinity
  });
}
export function useClassifications() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getClassificationsQueryOptions();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
const defaultFilters = {
  showCompleted: false,
  filterOutsideSla: false
};
const defaultColumnFilters = {
  status: undefined,
  type: undefined,
  assignedToUser: undefined,
  priority: undefined,
  tags: undefined,
  documentClassification: undefined,
  source: undefined,
  authorizationNumber: undefined
};
export function prefetchPaginatedTasksMetadata(queryClient: QueryClient) {
  queryClient.prefetchQuery({
    queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY, defaultFilters, defaultColumnFilters, 0, 30, "createdAt", "desc", undefined],
    queryFn: () => getPaginatedTasksMetadata({
      page: 0,
      pageSize: 30,
      showCompleted: defaultFilters.showCompleted,
      status: defaultColumnFilters.status,
      type: defaultColumnFilters.type,
      assignedToUser: defaultColumnFilters.assignedToUser,
      priority: defaultColumnFilters.priority,
      tags: defaultColumnFilters.tags,
      documentClassification: defaultColumnFilters.documentClassification,
      source: defaultColumnFilters.source,
      authorizationNumber: defaultColumnFilters.authorizationNumber,
      sortBy: "createdAt",
      sortOrder: "desc",
      filterOutsideSla: defaultFilters.filterOutsideSla
    })
  });
}
export function prefetchDocumentClassificationFilterSlugs(queryClient: QueryClient) {
  queryClient.prefetchQuery({
    queryKey: [TASK_CLASSIFICATION_FILTER_SLUGS_CACHE_KEY],
    queryFn: getDocumentClassificationFilterSlugs,
    staleTime: Infinity
  });
}
export function useDocumentClassificationFilterSlugs() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: [TASK_CLASSIFICATION_FILTER_SLUGS_CACHE_KEY],
      queryFn: getDocumentClassificationFilterSlugs,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}

/**
 * Custom hook for paginated task metadata fetching using the tasks_metadata view
 */
export function usePaginatedTasksMetadata(t0) {
  const $ = _c(23);
  const {
    filters,
    columnFilters,
    page: t1,
    pageSize: t2,
    prefetchNextPage: t3,
    sortBy,
    sortOrder,
    query
  } = t0 === undefined ? {} : t0;
  const page = t1 === undefined ? 0 : t1;
  const pageSize = t2 === undefined ? 30 : t2;
  const prefetchNextPage = t3 === undefined ? true : t3;
  const [defaultTaskFilters] = useTaskFilters();
  const queryClient = useQueryClient();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      filters: {
        mutationKey: CONTINUE_TASK_MUTATION_KEY,
        status: "pending"
      },
      select: _temp6
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const t5 = useMutationState(t4);
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      filters: {
        mutationKey: TASK_COMPLETION_MUTATION_KEY,
        status: "pending"
      },
      select: _temp7
    };
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const t7 = useMutationState(t6);
  let t8;
  if ($[2] !== t5 || $[3] !== t7) {
    t8 = [...t5, ...t7].filter(_temp8);
    $[2] = t5;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const pendingTaskCompletionIds = t8;
  const taskFilters = {
    ...defaultTaskFilters,
    ...filters
  };
  const tasksQuery = useQuery({
    queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY, taskFilters, columnFilters, page, pageSize, sortBy, sortOrder, query],
    queryFn: () => getPaginatedTasksMetadata({
      page,
      pageSize,
      showCompleted: taskFilters.showCompleted,
      filterOutsideSla: taskFilters.filterOutsideSla,
      status: columnFilters?.status,
      type: columnFilters?.type,
      assignedToUser: columnFilters?.assignedToUser,
      priority: columnFilters?.priority,
      tags: columnFilters?.tags,
      documentClassification: columnFilters?.documentClassification,
      source: columnFilters?.source,
      authorizationNumber: columnFilters?.authorizationNumber,
      sortBy,
      sortOrder,
      query
    }),
    staleTime: 5000,
    refetchInterval: 5000,
    placeholderData: _temp9
  });
  const paginatedData = tasksQuery.data;
  let t9;
  let tasks;
  let tasksMap;
  if ($[5] !== paginatedData?.tasks || $[6] !== pendingTaskCompletionIds) {
    tasksMap = paginatedData?.tasks || {};
    tasks = Object.values(tasksMap).filter(task => !pendingTaskCompletionIds.includes(task.id));
    t9 = Object.fromEntries(tasks.map(_temp10));
    $[5] = paginatedData?.tasks;
    $[6] = pendingTaskCompletionIds;
    $[7] = t9;
    $[8] = tasks;
    $[9] = tasksMap;
  } else {
    t9 = $[7];
    tasks = $[8];
    tasksMap = $[9];
  }
  const visibleTasksMap = t9;
  let t10;
  if ($[10] !== tasksMap) {
    t10 = Object.keys(tasksMap);
    $[10] = tasksMap;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const hiddenPendingTaskCount = t10.length - tasks.length;
  const hasMore = (!tasksQuery?.isFetching && paginatedData?.hasMore) ?? false;
  if (prefetchNextPage && hasMore) {
    queryClient.prefetchQuery({
      queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY, taskFilters, columnFilters, page + 1, pageSize, sortBy, sortOrder, query],
      queryFn: () => getPaginatedTasksMetadata({
        page: page + 1,
        pageSize,
        showCompleted: taskFilters.showCompleted,
        filterOutsideSla: taskFilters.filterOutsideSla,
        status: columnFilters?.status,
        type: columnFilters?.type,
        assignedToUser: columnFilters?.assignedToUser,
        priority: columnFilters?.priority,
        tags: columnFilters?.tags,
        documentClassification: columnFilters?.documentClassification,
        source: columnFilters?.source,
        authorizationNumber: columnFilters?.authorizationNumber,
        sortBy,
        sortOrder,
        query
      }),
      staleTime: 5000
    });
  }
  const t11 = Math.max(0, (paginatedData?.totalCount || 0) - hiddenPendingTaskCount);
  const t12 = paginatedData?.hasMore || false;
  const t13 = paginatedData?.page || page;
  const t14 = paginatedData?.pageSize || pageSize;
  let t15;
  if ($[12] !== t11 || $[13] !== t12 || $[14] !== t13 || $[15] !== t14 || $[16] !== tasks || $[17] !== tasksQuery.isError || $[18] !== tasksQuery.isFetching || $[19] !== tasksQuery.isLoading || $[20] !== tasksQuery.refetch || $[21] !== visibleTasksMap) {
    t15 = {
      tasksMap: visibleTasksMap,
      tasks,
      totalCount: t11,
      hasMore: t12,
      page: t13,
      pageSize: t14,
      isFetching: tasksQuery.isFetching,
      isLoading: tasksQuery.isLoading,
      isError: tasksQuery.isError,
      refetch: tasksQuery.refetch
    };
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
    $[15] = t14;
    $[16] = tasks;
    $[17] = tasksQuery.isError;
    $[18] = tasksQuery.isFetching;
    $[19] = tasksQuery.isLoading;
    $[20] = tasksQuery.refetch;
    $[21] = visibleTasksMap;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  return t15;
}

// Every facet's count is only ever consumed once its own filter dropdown is opened (to sort/badge
// its options), so each dimension is fetched independently and lazily. Pass `enabled` once the
// caller actually needs it (e.g. that filter's dropdown has been opened).
function _temp10(task_0) {
  return [task_0.id, task_0];
}
function _temp9(previousData) {
  return previousData;
}
function _temp8(taskId) {
  return taskId !== null;
}
function _temp7(mutation_0) {
  const variables_0 = mutation_0.state.variables as {
    taskId?: string;
    newStatus?: TaskStatus;
  } | undefined;
  if (!variables_0?.taskId) {
    return null;
  }
  if ("newStatus" in variables_0 && variables_0.newStatus !== TaskStatus.COMPLETED) {
    return null;
  }
  return variables_0.taskId;
}
function _temp6(mutation) {
  const variables = mutation.state.variables as {
    isFinalSection?: boolean;
    task?: {
      id?: string;
    };
  } | undefined;
  return variables?.isFinalSection ? variables.task?.id ?? null : null;
}
export function useTaskFacetCountQuery(dimension, t0, enabled) {
  const $ = _c(12);
  const filters = t0 === undefined ? defaultFilters : t0;
  let t1;
  if ($[0] !== dimension) {
    t1 = [dimension];
    $[0] = dimension;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== filters || $[3] !== t1) {
    t2 = [TASK_FACET_COUNTS_CACHE_KEY, filters, t1];
    $[2] = filters;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== dimension || $[6] !== filters) {
    t3 = () => getTaskFacetCounts({
      ...filters,
      facets: [dimension]
    });
    $[5] = dimension;
    $[6] = filters;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== enabled || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled,
      staleTime: 300000,
      refetchOnWindowFocus: false,
      placeholderData: _temp11
    };
    $[8] = enabled;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
}

/**
 * A patient's tasks as metadata rows, which is all the patient views render. Passing
 * `unconfirmedName` also matches tasks whose patient is unconfirmed but whose placeholder name is
 * this patient's.
 * @param patientId Omit to disable the query.
 */
function _temp11(previousData) {
  return previousData;
}
export function usePatientTaskMetadata(patientId, unconfirmedName) {
  const $ = _c(20);
  const patientGivenName = unconfirmedName?.givenName ?? undefined;
  const patientFamilyName = unconfirmedName?.familyName ?? undefined;
  let t0;
  let t1;
  if ($[0] !== patientFamilyName || $[1] !== patientGivenName || $[2] !== patientId) {
    t0 = [TASKS_CACHE_KEY, "patient", patientId, patientGivenName, patientFamilyName];
    t1 = () => getPaginatedTasksMetadata({
      page: 0,
      pageSize: PATIENT_TASKS_PAGE_SIZE,
      showCompleted: true,
      sortBy: "createdAt",
      sortOrder: "desc",
      patientId,
      patientGivenName,
      patientFamilyName
    });
    $[0] = patientFamilyName;
    $[1] = patientGivenName;
    $[2] = patientId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  const t2 = Boolean(patientId);
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity,
      refetchInterval: 5000
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const tasksQuery = useQuery(t3);
  let t4;
  if ($[9] !== tasksQuery.data?.tasks) {
    t4 = tasksQuery.data?.tasks || {};
    $[9] = tasksQuery.data?.tasks;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const patientTaskMetadataMap = t4;
  let t5;
  if ($[11] !== patientTaskMetadataMap) {
    t5 = Object.values(patientTaskMetadataMap);
    $[11] = patientTaskMetadataMap;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== patientTaskMetadataMap || $[14] !== t5 || $[15] !== tasksQuery.isError || $[16] !== tasksQuery.isFetching || $[17] !== tasksQuery.isLoading || $[18] !== tasksQuery.refetch) {
    t6 = {
      patientTaskMetadataMap,
      patientTaskMetadata: t5,
      isFetching: tasksQuery.isFetching,
      isLoading: tasksQuery.isLoading,
      isError: tasksQuery.isError,
      refetch: tasksQuery.refetch
    };
    $[13] = patientTaskMetadataMap;
    $[14] = t5;
    $[15] = tasksQuery.isError;
    $[16] = tasksQuery.isFetching;
    $[17] = tasksQuery.isLoading;
    $[18] = tasksQuery.refetch;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
export function useFileTasks(fileId) {
  const $ = _c(17);
  let t0;
  let t1;
  if ($[0] !== fileId) {
    t0 = [TASKS_CACHE_KEY, "file", fileId];
    t1 = () => {
      if (!fileId) {
        return {} as Awaited<ReturnType<typeof getAllFileTasks>>;
      }
      return getAllFileTasks(fileId);
    };
    $[0] = fileId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(fileId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity,
      refetchInterval: 5000
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const tasksQuery = useQuery(t3);
  let t4;
  let t5;
  if ($[7] !== tasksQuery.data) {
    const fileTasksMap = tasksQuery.data || {};
    t4 = fileTasksMap;
    t5 = Object.values(fileTasksMap).sort(_temp12);
    $[7] = tasksQuery.data;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== tasksQuery.isError || $[13] !== tasksQuery.isFetching || $[14] !== tasksQuery.isLoading || $[15] !== tasksQuery.refetch) {
    t6 = {
      fileTasksMap: t4,
      fileTasks: t5,
      isFetching: tasksQuery.isFetching,
      isLoading: tasksQuery.isLoading,
      isError: tasksQuery.isError,
      refetch: tasksQuery.refetch
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = tasksQuery.isError;
    $[13] = tasksQuery.isFetching;
    $[14] = tasksQuery.isLoading;
    $[15] = tasksQuery.refetch;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}

/**
 * Hook to get matched patient tasks for a section
 * NOTE: Unlike old patient-section, this hook does not search over ALL tasks for placeholder patient name matches;
 * it only searches over the patient tasks for the matched patient, if there is one.
 */
function _temp12(a, b) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
export function useMatchedPatientTasksForSection(section, taskDisplayId) {
  const $ = _c(10);
  let t0;
  bb0: {
    const patientBlock = section?.enrichedContent?.[BlockKey.PATIENT];
    if (!patientBlock) {
      t0 = null;
      break bb0;
    }
    const reviewedFields = patientBlock.reviewedFields;
    t0 = reviewedFields?.id ? reviewedFields as Patient : null;
  }
  const matchedPatient = t0;
  const {
    patientTaskMetadata,
    isLoading: isLoadingPatientTasks
  } = usePatientTaskMetadata(matchedPatient?.id);
  let t1;
  bb1: {
    if (!patientTaskMetadata) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb1;
    }
    let t2;
    if ($[1] !== patientTaskMetadata || $[2] !== taskDisplayId) {
      let t3;
      if ($[4] !== taskDisplayId) {
        t3 = task => !completedStatuses.includes(task.status as TaskStatus) && task.displayId !== taskDisplayId;
        $[4] = taskDisplayId;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      t2 = patientTaskMetadata.filter(t3);
      $[1] = patientTaskMetadata;
      $[2] = taskDisplayId;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const verifiedTasks = t1;
  const matchedTasksUrl = matchedPatient ? `/patients/${matchedPatient.displayId}/tasks` : null;
  let t2;
  if ($[6] !== isLoadingPatientTasks || $[7] !== matchedTasksUrl || $[8] !== verifiedTasks.length) {
    t2 = {
      isLoading: isLoadingPatientTasks,
      totalMatchedTasks: verifiedTasks.length,
      matchedTasksUrl
    };
    $[6] = isLoadingPatientTasks;
    $[7] = matchedTasksUrl;
    $[8] = verifiedTasks.length;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function useTaskByDisplayId(displayId) {
  const $ = _c(17);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  if ($[0] !== displayId) {
    t0 = [TASK_CACHE_KEY, displayId];
    $[0] = displayId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== displayId || $[3] !== queryClient || $[4] !== taskFilters) {
    t1 = async () => {
      const filters = taskFilters;
      ;
      try {
        const task = await getTaskById(displayId);
        if (task) {
          updateTasksQueryData(queryClient, task, filters);
        }
        return task;
      } catch (t2) {
        const err = t2;
        if (isNotFound(err as Error)) {
          return null;
        }
        throw err;
      }
    };
    $[2] = displayId;
    $[3] = queryClient;
    $[4] = taskFilters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== queryClient) {
    t2 = query => {
      if (query.state.data?.status === TaskStatus.PROCESSING) {
        return TASK_AGENT_RUN_POLL_INTERVAL_MS;
      }
      if (query.state.data?.type !== TaskType.INTAKE) {
        return false;
      }
      return isTaskAgentRunActive(queryClient, query.state.data.id) ? TASK_AGENT_RUN_POLL_INTERVAL_MS : false;
    };
    $[6] = queryClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      refetchInterval: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data: task_0,
    isFetching,
    isLoading,
    isError
  } = useQuery(t3);
  let t4;
  if ($[12] !== isError || $[13] !== isFetching || $[14] !== isLoading || $[15] !== task_0) {
    t4 = {
      task: task_0,
      isFetching,
      isLoading,
      isError
    };
    $[12] = isError;
    $[13] = isFetching;
    $[14] = isLoading;
    $[15] = task_0;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
export function useTaskById(id, options) {
  const $ = _c(15);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  if ($[0] !== id) {
    t0 = [TASK_BY_ID_CACHE_KEY, id];
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== id || $[3] !== queryClient || $[4] !== taskFilters) {
    t1 = async () => {
      if (!id) {
        return null;
      }
      const task = await getTaskById(id);
      if (task) {
        updateTasksQueryData(queryClient, task, taskFilters);
      }
      return task;
    };
    $[2] = id;
    $[3] = queryClient;
    $[4] = taskFilters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = options?.enabled !== false && !!id;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: Infinity,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data: task_0,
    isFetching,
    isLoading,
    isError
  } = useQuery(t3);
  let t4;
  if ($[10] !== isError || $[11] !== isFetching || $[12] !== isLoading || $[13] !== task_0) {
    t4 = {
      task: task_0,
      isFetching,
      isLoading,
      isError
    };
    $[10] = isError;
    $[11] = isFetching;
    $[12] = isLoading;
    $[13] = task_0;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export const useTasksMetadata = t0 => {
  const $ = _c(26);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    filters,
    columnFilters,
    sortBy,
    sortOrder,
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[2] !== columnFilters || $[3] !== filters || $[4] !== sortBy || $[5] !== sortOrder) {
    t3 = ["tasksMetadata", filters, columnFilters, sortBy, sortOrder];
    $[2] = columnFilters;
    $[3] = filters;
    $[4] = sortBy;
    $[5] = sortOrder;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== columnFilters?.assignedToUser || $[8] !== columnFilters?.priority || $[9] !== columnFilters?.status || $[10] !== columnFilters?.type || $[11] !== filters?.showCompleted || $[12] !== sortBy || $[13] !== sortOrder) {
    t4 = () => getTasksMetadata({
      showCompleted: !!filters?.showCompleted,
      status: columnFilters?.status,
      type: columnFilters?.type,
      assignedToUser: columnFilters?.assignedToUser,
      priority: columnFilters?.priority,
      sortBy,
      sortOrder: sortOrder ?? "desc"
    });
    $[7] = columnFilters?.assignedToUser;
    $[8] = columnFilters?.priority;
    $[9] = columnFilters?.status;
    $[10] = columnFilters?.type;
    $[11] = filters?.showCompleted;
    $[12] = sortBy;
    $[13] = sortOrder;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== enabled || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      enabled,
      queryKey: t3,
      queryFn: t4,
      staleTime: 60000,
      placeholderData: _temp13
    };
    $[15] = enabled;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const query = useQuery(t5);
  let t6;
  if ($[19] !== query.data || $[20] !== query.error || $[21] !== query.isError || $[22] !== query.isFetching || $[23] !== query.isLoading || $[24] !== query.refetch) {
    t6 = {
      data: query.data,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[19] = query.data;
    $[20] = query.error;
    $[21] = query.isError;
    $[22] = query.isFetching;
    $[23] = query.isLoading;
    $[24] = query.refetch;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};

/**
 * Creates a generic onMutate handler for optimistic task updates.
 * This handler updates all relevant caches: the main tasks list,
 * the paginated metadata list, and individual task queries.
 * TODO: Once we completely get rid of getAllTasks, we can simplify the first half of the update func.
 *  and implement rollbacks for specific pages.
 */
const createOptimisticTaskUpdateHandler = <T extends {
  taskId: string;
},>(queryClient: QueryClient, filters: TaskFilters, updateFns: {
  /**
   * A function that computes the changes for the full Task object.
   * It will only be called if the task is found in the `TASKS_CACHE_KEY`.
   */
  task?: (variables: T, originalTask: Task) => Partial<Task>;
  /**
   * A function that computes the changes for the TaskMetadata object.
   * It will be called for each instance of the task found in any
   */
  metadata?: (variables: T, originalMetadata: TaskMetadata) => Partial<TaskMetadata>;
}) => {
  return async (variables: T) => {
    await queryClient.cancelQueries({
      queryKey: [TASKS_CACHE_KEY]
    });
    await queryClient.cancelQueries({
      queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
    });

    // Snapshot the full task list for rollback.
    const previousTasks = queryClient.getQueryData<Record<string, Task>>([TASKS_CACHE_KEY, filters]);

    // Handle TASKS_CACHE_KEY updates (TODO: deprecate) and individual task caches
    if (updateFns.task) {
      const originalTask = previousTasks?.[variables.taskId];
      if (originalTask) {
        const taskChanges = updateFns.task(variables, originalTask);
        const updatedTask = {
          ...originalTask,
          ...taskChanges
        };

        // Use the shared updateTasksQueryData function
        updateTasksQueryData(queryClient, updatedTask, filters);
      }
    }

    // Handle PAGINATED_TASKS_METADATA_CACHE_KEY updates
    if (updateFns.metadata) {
      const paginatedQueries = queryClient.getQueryCache().findAll({
        queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
      });
      paginatedQueries.forEach(query => {
        const originalData = queryClient.getQueryData<PaginatedTasksMetadataResponse>(query.queryKey);
        const originalMetadata = originalData?.tasks?.[variables.taskId];
        if (originalMetadata) {
          const metadataChanges = updateFns.metadata?.(variables, originalMetadata);
          queryClient.setQueryData<PaginatedTasksMetadataResponse>(query.queryKey, oldData => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              tasks: {
                ...oldData.tasks,
                [variables.taskId]: {
                  ...originalMetadata,
                  ...metadataChanges
                }
              }
            };
          });
        }
      });
    }
    return {
      previousTasks
    };
  };
};

/**
 * Updates the tasks query data with a new or updated task.
 * @param queryClient - The QueryClient instance.
 * @param updatedTask - The task to add or update in the cache.
 */
export const updateTasksQueryData = (queryClient: QueryClient, updatedTask: Task, filters: TaskFilters) => {
  // Update non-paginated tasks cache
  queryClient.setQueryData([TASKS_CACHE_KEY, filters], (oldData: Record<string, Task> = {}) => {
    return {
      ...oldData,
      [updatedTask.id]: updatedTask
    };
  });

  // Update individual task caches
  queryClient.setQueryData([TASK_CACHE_KEY, updatedTask.displayId], updatedTask);
  queryClient.setQueryData([TASK_BY_ID_CACHE_KEY, updatedTask.id], updatedTask);
};

/**
 * Invalidates all task-related caches that can be impacted by updates.
 */
export const invalidateTaskCaches = (queryClient: QueryClient, task?: {
  id?: string | null;
  displayId?: string | null;
}) => {
  const invalidations = [queryClient.invalidateQueries({
    queryKey: [TASKS_CACHE_KEY]
  })];
  if (task?.displayId) {
    invalidations.push(queryClient.invalidateQueries({
      queryKey: [TASK_CACHE_KEY, task.displayId]
    }));
  }
  if (task?.id) {
    invalidations.push(queryClient.invalidateQueries({
      queryKey: [TASK_BY_ID_CACHE_KEY, task.id]
    }));
  }
  invalidations.push(queryClient.invalidateQueries({
    queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
  }));
  invalidations.push(queryClient.invalidateQueries({
    queryKey: [TASK_FACET_COUNTS_CACHE_KEY]
  }));
  return Promise.all(invalidations);
};

// Records the scheduling-eligibility verification and completes the verification task in one call.
// On success the task caches are invalidated so the (now completed) task drops out of the queue.
export function useTaskSchedulingRecommendation(taskId, taskUpdatedAt) {
  const $ = _c(15);
  let t0;
  if ($[0] !== taskId || $[1] !== taskUpdatedAt) {
    t0 = [...getTaskSchedulingRecommendationQueryKey(taskId), taskUpdatedAt];
    $[0] = taskId;
    $[1] = taskUpdatedAt;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryKey = t0 as const;
  let t1;
  if ($[3] !== taskId) {
    t1 = async () => {
      if (!taskId) {
        return null;
      }
      return getTaskSchedulingRecommendation(taskId);
    };
    $[3] = taskId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = Boolean(taskId);
  let t3;
  if ($[5] !== queryKey || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchInterval: 30000
    };
    $[5] = queryKey;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const query = useQuery(t3);
  const t4 = query.data ?? null;
  let t5;
  if ($[9] !== query.isError || $[10] !== query.isFetching || $[11] !== query.isPending || $[12] !== queryKey || $[13] !== t4) {
    t5 = {
      queryKey,
      recommendation: t4,
      isPending: query.isPending,
      isFetching: query.isFetching,
      isError: query.isError
    };
    $[9] = query.isError;
    $[10] = query.isFetching;
    $[11] = query.isPending;
    $[12] = queryKey;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
export function useRegenerateTaskSchedulingRecommendation(taskId) {
  const $ = _c(11);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== taskId) {
    t0 = async () => regenerateTaskSchedulingRecommendation(taskId);
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient || $[3] !== taskId) {
    t1 = recommendation => {
      queryClient.setQueriesData({
        queryKey: getTaskSchedulingRecommendationQueryKey(taskId)
      }, recommendation);
      toast({
        title: "Recommendation regenerated",
        description: "The task now uses the new scheduling recommendation."
      });
    };
    $[2] = queryClient;
    $[3] = taskId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== taskId) {
    t2 = error => {
      logger.error("Error regenerating task scheduling recommendation", {
        taskId,
        error
      });
      toast({
        title: "Couldn't regenerate recommendation",
        description: "Please try again.",
        variant: "destructive"
      });
    };
    $[5] = taskId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useMutation(t3);
}
export function useVerifySchedulingEligibilityTask(taskId, displayId) {
  const $ = _c(13);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== taskId) {
    t0 = async payload => verifySchedulingEligibilityTask(taskId, payload);
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== displayId || $[3] !== queryClient || $[4] !== taskId) {
    t1 = (updatedTask, payload_0) => {
      invalidateTaskCaches(queryClient, {
        id: updatedTask.id,
        displayId: updatedTask.displayId ?? displayId
      });
      queryClient.setQueriesData({
        queryKey: getTaskSchedulingRecommendationQueryKey(taskId)
      }, existing => existing?.id === payload_0.recommendationId ? {
        ...existing,
        isCorrect: payload_0.isCorrect,
        verificationReason: payload_0.verificationReason
      } : existing);
      toast({
        title: "Verification saved",
        description: "The task has been completed."
      });
    };
    $[2] = displayId;
    $[3] = queryClient;
    $[4] = taskId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== queryClient || $[7] !== taskId) {
    t2 = error => {
      queryClient.invalidateQueries({
        queryKey: getTaskSchedulingRecommendationQueryKey(taskId)
      });
      logger.error("Error verifying scheduling eligibility task", {
        taskId,
        error
      });
      toast({
        title: "Couldn't save verification",
        description: error.message,
        variant: "destructive"
      });
    };
    $[6] = queryClient;
    $[7] = taskId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2
    };
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useMutation(t3);
}

// Creates a common onError handler for task mutations
const createTaskErrorHandler = (queryClient: QueryClient, filters: TaskFilters, errorMessage: string) => {
  return (error: Error, variables: any, context: any) => {
    logger.error(`Error: ${errorMessage}`);

    // Restore previous data for non-paginated tasks
    if (context?.previousTasks) {
      queryClient.setQueryData([TASKS_CACHE_KEY, filters], context.previousTasks);
    }

    // For paginated tasks, we need to invalidate since we don't store previous paginated state
    // This ensures fresh data is fetched after rollback
    queryClient.invalidateQueries({
      queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
    });
    toast({
      title: errorMessage,
      description: error.message,
      variant: "destructive"
    });
  };
};

// Creates a success toast with an optional action button to view the task
const createTaskSuccessToast = (title: string, description: (data: Task, variables: any) => string) => {
  return (data: Task, variables: any) => {
    toast({
      title,
      description: description(data, variables),
      action: <ToastAction altText="View task" onClick={() => window.location.href = `/tasks/${data.displayId}`}>
          View task
        </ToastAction>
    });
  };
};
export function useTaskUpdateMutation() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = task_0 => {
      const handler = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
        task: () => task_0,
        metadata: () => taskToTaskMetadata(task_0)
      });
      return handler({
        ...task_0,
        taskId: task_0.id
      });
    };
    t1 = createTaskErrorHandler(queryClient, taskFilters, "Something went wrong updating the task. Please refresh the page and try again.");
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: _temp14,
      onMutate: t0,
      onError: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
}
function _temp14(task) {
  return updateTask(task);
}
export function useResetSplitDocumentConfidenceMutation(task) {
  const $ = _c(12);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== task || $[2] !== taskFilters) {
    t0 = async fileId_0 => {
      await Promise.all([queryClient.cancelQueries({
        queryKey: [TASKS_CACHE_KEY]
      }), queryClient.cancelQueries({
        queryKey: [TASK_CACHE_KEY, task.displayId]
      }), queryClient.cancelQueries({
        queryKey: [TASK_BY_ID_CACHE_KEY, task.id]
      })]);
      const previousTasks = queryClient.getQueryData([TASKS_CACHE_KEY, taskFilters]);
      const previousTaskByDisplayId = queryClient.getQueryData([TASK_CACHE_KEY, task.displayId]);
      const previousTaskById = queryClient.getQueryData([TASK_BY_ID_CACHE_KEY, task.id]);
      const currentTask = previousTaskByDisplayId ?? previousTaskById ?? task;
      const updatedTask = {
        ...currentTask,
        files: currentTask.files.map(file => file.id === fileId_0 ? {
          ...file,
          reviewedSplitDocumentConfidence: null,
          intakeSectionGrouping: null,
          reviewedIntakeSectionGrouping: null
        } : file)
      };
      updateTasksQueryData(queryClient, updatedTask, taskFilters);
      return {
        previousTasks,
        previousTaskByDisplayId,
        previousTaskById
      };
    };
    t1 = (_error, _fileId, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData([TASKS_CACHE_KEY, taskFilters], context.previousTasks);
      }
      if (context?.previousTaskByDisplayId) {
        queryClient.setQueryData([TASK_CACHE_KEY, task.displayId], context.previousTaskByDisplayId);
      }
      if (context?.previousTaskById) {
        queryClient.setQueryData([TASK_BY_ID_CACHE_KEY, task.id], context.previousTaskById);
      }
      toast({
        title: "Error",
        description: "Failed to revert classification changes. Please try again.",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = task;
    $[2] = taskFilters;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[5] !== queryClient || $[6] !== task) {
    t2 = () => invalidateTaskCaches(queryClient, {
      id: task.id,
      displayId: task.displayId
    });
    $[5] = queryClient;
    $[6] = task;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      mutationFn: _temp15,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useMutation(t3);
}
function _temp15(fileId) {
  return resetSplitDocumentConfidence(fileId);
}
export function useLocalTaskUpdateMutation(options) {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = task_0 => {
      const handler = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
        task: () => task_0,
        metadata: () => taskToTaskMetadata(task_0)
      });
      return handler({
        ...task_0,
        taskId: task_0.id
      });
    };
    t1 = createTaskErrorHandler(queryClient, taskFilters, "Something went wrong updating the task. Please refresh the page and try again.");
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options?.invalidateOnSettled || $[5] !== queryClient) {
    t2 = data => {
      if (options?.invalidateOnSettled === false) {
        return;
      }
      invalidateTaskCaches(queryClient, {
        id: data?.id,
        displayId: data?.displayId
      });
    };
    $[4] = options?.invalidateOnSettled;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      mutationFn: _temp16,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useMutation(t3);
}
function _temp16(task) {
  return Promise.resolve(task);
}
export function useTaskStatusMutation() {
  const $ = _c(12);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: _temp18,
      metadata: _temp19
    });
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== queryClient || $[4] !== taskFilters) {
    t1 = createTaskErrorHandler(queryClient, taskFilters, "Something went wrong updating the status. Please refresh the page and try again.");
    $[3] = queryClient;
    $[4] = taskFilters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== queryClient) {
    t2 = (data_1, _error, variables_0) => invalidateTaskCaches(queryClient, {
      id: variables_0?.taskId,
      displayId: data_1?.displayId
    });
    $[6] = queryClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      mutationKey: TASK_COMPLETION_MUTATION_KEY,
      mutationFn: _temp17,
      onMutate: t0,
      onSuccess: _temp21,
      onError: t1,
      onSettled: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useMutation(t3);
}
function _temp21(data, variables) {
  if (data.status !== TaskStatus.COMPLETED && data.status !== TaskStatus.PROCESSING) {
    createTaskSuccessToast("Status updated", _temp20)(data, variables);
  }
}
function _temp20(data_0) {
  return `${data_0.displayId} status changed to ${pastTenseStatus[data_0.status]}`;
}
function _temp19(t0) {
  const {
    newStatus: newStatus_1
  } = t0;
  return {
    status: newStatus_1
  };
}
function _temp18(t0) {
  const {
    newStatus: newStatus_0
  } = t0;
  return {
    status: newStatus_0
  };
}
function _temp17(t0) {
  const {
    taskId,
    newStatus
  } = t0;
  return updateStatus({
    taskId,
    newStatus
  });
}
export function useVerifyAndCompleteMutation(t0) {
  const $ = _c(7);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onError
  } = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== queryClient) {
    t2 = (_data, _error, t3) => {
      const {
        taskId: taskId_0
      } = t3;
      return invalidateTaskCaches(queryClient, {
        id: taskId_0
      });
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onError || $[5] !== t2) {
    t3 = {
      mutationKey: TASK_COMPLETION_MUTATION_KEY,
      mutationFn: _temp22,
      onError,
      onSettled: t2
    };
    $[4] = onError;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useMutation(t3);
}
function _temp22(t0) {
  const {
    taskId,
    verificationResult,
    reason,
    details
  } = t0;
  return verifyAndComplete(taskId, {
    verificationResult,
    reason,
    details
  });
}
export function useSendReferringProviderEmailMutation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp23
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp23(t0) {
  const {
    taskId
  } = t0;
  return sendReferringProviderEmail(taskId);
}
export function useAssignedUserMutation() {
  const $ = _c(12);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  let t1;
  let t2;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = variables => {
      if (variables.overwrite === false) {
        return {};
      }
      const handler = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
        task: _temp25,
        metadata: _temp26
      });
      return handler({
        ...variables,
        taskId: variables.taskId
      });
    };
    t1 = data => {
      updateTasksQueryData(queryClient, data, taskFilters);
    };
    t2 = (error, variables_0, context) => {
      queryClient.invalidateQueries({
        queryKey: [TASKS_CACHE_KEY, taskFilters]
      });
      if (variables_0.overwrite === false) {
        toast({
          title: "Task already assigned",
          description: "Someone else is already working on this task. Please select a different task.",
          variant: "default"
        });
        return;
      }
      return createTaskErrorHandler(queryClient, taskFilters, "Assignment update failed")(error, variables_0, context);
    };
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t0 = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== queryClient) {
    t3 = () => {
      queryClient.invalidateQueries({
        queryKey: [TASK_FACET_COUNTS_CACHE_KEY]
      });
      queryClient.invalidateQueries({
        queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
      });
      queryClient.invalidateQueries({
        queryKey: [TASK_CACHE_KEY]
      });
    };
    $[5] = queryClient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: _temp24,
      onMutate: t0,
      onSuccess: t1,
      onError: t2,
      onSettled: t3
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useMutation(t4);
}
function _temp26(vars_0) {
  return {
    currentAssignedToUserId: vars_0.userId,
    assignedAt: new Date().toISOString()
  };
}
function _temp25(vars, originalTask) {
  return {
    currentAssignedToUserId: vars.userId,
    originalAssignedToUserId: originalTask.originalAssignedToUserId ?? vars.userId,
    assignedAt: new Date().toISOString()
  };
}
async function _temp24(t0) {
  const {
    taskId,
    userId,
    overwrite: t1
  } = t0;
  const overwrite = t1 === undefined ? true : t1;
  const task = await updateAssignedUser({
    taskId,
    userId,
    overwrite
  });
  if (!task) {
    throw new Error("Task is already assigned to another user.");
  }
  return task;
}
export function useUnassignAllMyTasksMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp27,
      onSuccess: _temp28,
      onError: _temp29,
      onSettled: () => Promise.all([queryClient.invalidateQueries({
        queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
      }), queryClient.invalidateQueries({
        queryKey: [TASK_FACET_COUNTS_CACHE_KEY]
      }), queryClient.invalidateQueries({
        queryKey: [TASK_CACHE_KEY]
      }), queryClient.invalidateQueries({
        queryKey: [TASKS_CACHE_KEY]
      })])
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp29() {
  toast({
    title: "Couldn't unassign tasks",
    description: "Please refresh the page and try again.",
    variant: "destructive"
  });
}
function _temp28(unassignedTaskCount) {
  toast({
    title: unassignedTaskCount === 0 ? "No matching tasks" : "Tasks unassigned",
    description: unassignedTaskCount === 0 ? "You aren't assigned to any open, failed, or paused tasks in this customer." : `You were unassigned from ${unassignedTaskCount} ${unassignedTaskCount === 1 ? "task" : "tasks"}.`
  });
}
function _temp27() {
  return unassignAllMyTasks();
}
export function useTaskPriorityMutation() {
  const $ = _c(13);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: _temp31,
      metadata: _temp32
    });
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createTaskSuccessToast("Task priority updated", _temp33);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== queryClient || $[5] !== taskFilters) {
    t2 = createTaskErrorHandler(queryClient, taskFilters, "Priority update failed");
    $[4] = queryClient;
    $[5] = taskFilters;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== queryClient) {
    t3 = (data_0, _error, variables) => {
      invalidateTaskCaches(queryClient, {
        id: variables?.taskId,
        displayId: data_0?.displayId
      });
    };
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      mutationFn: _temp30,
      onMutate: t0,
      onSuccess: t1,
      onError: t2,
      onSettled: t3
    };
    $[9] = t0;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return useMutation(t4);
}
function _temp33(data) {
  return `${data.displayId} priority updated to ${data.priority}`;
}
function _temp32(t0) {
  const {
    priority: priority_1
  } = t0;
  return {
    taskPriority: priority_1
  };
}
function _temp31(t0) {
  const {
    priority: priority_0
  } = t0;
  return {
    priority: priority_0
  };
}
function _temp30(t0) {
  const {
    taskId,
    priority
  } = t0;
  return updatePriority({
    taskId,
    priority
  });
}
export function useUpdateTaskNoteMutation() {
  const $ = _c(12);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: _temp35,
      metadata: _temp36
    });
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== queryClient) {
    t1 = (task, t2) => {
      const {
        note: note_2
      } = t2;
      if (note_2 === null || note_2 === "") {
        toast({
          title: "Note deleted successfully."
        });
      }
      if (!task.patientOrder) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [PAGINATED_ORDERS_CACHE_KEY]
      });
      queryClient.invalidateQueries({
        queryKey: [SINGLE_ORDER_CACHE_KEY]
      });
      queryClient.invalidateQueries({
        queryKey: [TASK_CACHE_KEY]
      });
      queryClient.invalidateQueries({
        queryKey: [TASK_BY_ID_CACHE_KEY]
      });
    };
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== queryClient || $[6] !== taskFilters) {
    t2 = createTaskErrorHandler(queryClient, taskFilters, "Note update failed");
    $[5] = queryClient;
    $[6] = taskFilters;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      mutationFn: _temp34,
      onMutate: t0,
      onSuccess: t1,
      onError: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useMutation(t3);
}
function _temp36(t0) {
  const {
    note: note_1
  } = t0;
  return {
    note: note_1
  };
}
function _temp35(t0, originalTask) {
  const {
    note: note_0
  } = t0;
  if (originalTask.patientOrder) {
    return {
      patientOrder: {
        ...originalTask.patientOrder,
        note: note_0
      }
    };
  }
  const updatedFiles = originalTask.files.map(file => file.type === FileType.INBOUND ? {
    ...file,
    note: note_0
  } : file);
  return {
    files: updatedFiles
  };
}
function _temp34(t0) {
  const {
    taskId,
    note
  } = t0;
  return updateTaskNote({
    taskId,
    note
  });
}
export function useTaskTagsMutation() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: _temp38,
      metadata: _temp39
    });
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== queryClient || $[4] !== taskFilters) {
    t1 = createTaskErrorHandler(queryClient, taskFilters, "Tag update failed");
    $[3] = queryClient;
    $[4] = taskFilters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = {
      mutationFn: _temp37,
      onMutate: t0,
      onError: t1
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useMutation(t2);
}
function _temp39(t0) {
  const {
    tags: tags_1
  } = t0;
  return {
    tags: tags_1.length > 0 ? tags_1 : null
  };
}
function _temp38(t0) {
  const {
    tags: tags_0
  } = t0;
  return {
    tags: tags_0.length > 0 ? tags_0 : null
  };
}
function _temp37(t0) {
  const {
    taskId,
    tags
  } = t0;
  return updateTaskTags({
    taskId,
    tags
  });
}
export const TASK_HISTORY_CACHE_KEY = "task-history";
export function useTaskHistory(taskId, options) {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== taskId) {
    t0 = [TASK_HISTORY_CACHE_KEY, taskId];
    t1 = () => {
      if (!taskId) {
        return null;
      }
      return getTaskHistory(taskId);
    };
    $[0] = taskId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = options?.enabled !== false && !!taskId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 30000
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
}
export function useDeleteTaskMutation() {
  const $ = _c(10);
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();
  let t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== taskFilters) {
    t0 = async t2 => {
      const {
        taskId: taskId_0
      } = t2;
      await queryClient.cancelQueries({
        queryKey: [TASKS_CACHE_KEY]
      });
      await queryClient.cancelQueries({
        queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
      });
      const previousTasks = queryClient.getQueryData([TASKS_CACHE_KEY, taskFilters]) || {};
      queryClient.setQueryData([TASKS_CACHE_KEY, taskFilters], t3 => {
        const tasks = t3 === undefined ? {} : t3;
        const updatedTasks = {
          ...tasks
        };
        delete updatedTasks[taskId_0];
        return updatedTasks;
      });
      const paginatedMetadataQueries = queryClient.getQueryCache().findAll({
        queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
      });
      paginatedMetadataQueries.forEach(query => {
        const queryData = queryClient.getQueryData(query.queryKey);
        if (queryData?.tasks && queryData.tasks[taskId_0]) {
          const updatedTasks_0 = {
            ...queryData.tasks
          };
          delete updatedTasks_0[taskId_0];
          const updatedPaginatedMetadataData = {
            ...queryData,
            tasks: updatedTasks_0
          };
          queryClient.setQueryData(query.queryKey, updatedPaginatedMetadataData);
        }
      });
      queryClient.invalidateQueries({
        queryKey: [TASKS_CACHE_KEY],
        refetchType: "inactive"
      });
      return {
        previousTasks
      };
    };
    t1 = (error, t4, context) => {
      const {
        taskId: taskId_1
      } = t4;
      if (context) {
        queryClient.setQueryData([TASKS_CACHE_KEY, taskFilters], context.previousTasks);
        queryClient.invalidateQueries({
          queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY]
        });
      }
      logger.error("Error deleting task:", {
        taskId: taskId_1,
        error
      });
      toast({
        title: "Task deletion failed",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = taskFilters;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: [TASK_FACET_COUNTS_CACHE_KEY]
      });
    };
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: _temp40,
      onMutate: t0,
      onError: t1,
      onSettled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useMutation(t3);
}
function _temp40(t0) {
  const {
    taskId
  } = t0;
  return deleteTask({
    taskId
  });
}
export const CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY = "can-resume-awaiting-information";
const WORKFLOW_STEP_RESUME_CACHE_KEY = "workflow-step-resume";
export function useWorkflowStepResumeCheck(task) {
  const $ = _c(8);
  let t0;
  if ($[0] !== task.id || $[1] !== task.updatedAt) {
    t0 = [WORKFLOW_STEP_RESUME_CACHE_KEY, task.id, task.updatedAt];
    $[0] = task.id;
    $[1] = task.updatedAt;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== task.id) {
    t1 = () => getWorkflowStepResumeCheck(task.id);
    $[3] = task.id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
}
export function useResumeWorkflowStepMutation(task) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== task) {
    t0 = taskStepId => resumeWorkflowStep(task.id, {
      taskStepId
    });
    $[0] = task;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient || $[3] !== task) {
    t1 = async () => {
      await Promise.all([invalidateTaskCaches(queryClient, task), queryClient.invalidateQueries({
        queryKey: [WORKFLOW_STEP_RESUME_CACHE_KEY, task.id]
      }), queryClient.invalidateQueries({
        queryKey: [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY, task.id]
      })]);
    };
    $[2] = queryClient;
    $[3] = task;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSettled: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}

/** Only meaningful for a task sitting at AWAITING_INFORMATION; pass `enabled: false` otherwise. */
export function useCanResumeAwaitingInformationTask(taskId, options) {
  const $ = _c(11);
  let t0;
  let t1;
  if ($[0] !== taskId) {
    t0 = [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY, taskId];
    t1 = () => getCanResumeAwaitingInformationTask(taskId);
    $[0] = taskId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data,
    isLoading
  } = useQuery(t3);
  const t4 = data?.resumable ?? false;
  const t5 = data?.blockedReason;
  let t6;
  if ($[7] !== isLoading || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      resumable: t4,
      blockedReason: t5,
      isLoading
    };
    $[7] = isLoading;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
export function useResumeAwaitingInformationTaskMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp41,
      onSuccess: (result, taskId_0) => {
        queryClient.invalidateQueries({
          queryKey: [TASK_CACHE_KEY, taskId_0]
        });
        queryClient.invalidateQueries({
          queryKey: [TASK_BY_ID_CACHE_KEY, taskId_0]
        });
        queryClient.invalidateQueries({
          queryKey: [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY, taskId_0]
        });
        if (result.blockedReason) {
          toast({
            title: "Task not resumed",
            description: result.blockedReason,
            variant: "destructive"
          });
        } else {
          invalidateTaskCaches(queryClient, {
            id: taskId_0
          });
        }
      },
      onError: _temp42
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp42(error, taskId_1) {
  logger.error("Error resuming awaiting-information task:", {
    taskId: taskId_1,
    error
  });
  toast({
    title: "Failed to resume task",
    description: "Please refresh the page and try again.",
    variant: "destructive"
  });
}
function _temp41(taskId) {
  return resumeAwaitingInformationTask(taskId);
}
export function useConfirmInsuranceNetworkMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp43,
      onSuccess: (_data, t1) => {
        const {
          taskId: taskId_0,
          displayId
        } = t1;
        return invalidateTaskCaches(queryClient, {
          id: taskId_0,
          displayId
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp43(t0) {
  const {
    taskId,
    scope
  } = t0;
  return confirmInsuranceNetwork(taskId, scope);
}
function _temp13(previousData) {
  return previousData;
}